import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '../utils/errorUtils';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  MenuItem,
  InputLabel,
  Alert,
  Fade,
  Card,
  Button,
  CircularProgress,
  TextField,
  Select,
  FormControl,
} from '@mui/material';
import {
  Business,
  Person,
  ShoppingCart,
  Payment,
  Description,
  Check,
  Close as CloseIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { useForm, Controller } from 'react-hook-form';

import StepperProgress from './shared/StepperProgress';
import FormStep from './shared/FormStep';
import SmartTextField from './shared/SmartTextField';
import StepNavigation from './shared/StepNavigation';
import { StyledFormControl, StyledSelect } from './shared/StyledFormComponents';
import { api } from '../services/api';
import { useRAG } from '../hooks/useRAG';

interface AppleStylePurchaseContractFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (result: any) => void;
  onPreview: (result: any) => void;
  editMode?: boolean;
  existingData?: any;
}

// Function to fetch AI-generated sample data
const fetchAISampleData = async (contractType: string, contractSubtype: string): Promise<Partial<FormData>> => {
  try {
    const response = await api.post('/documents/generate-sample-data', {
      contract_type: contractType,
      contract_subtype: contractSubtype
    });
    
    if (response.data && response.data.sample_data) {
      return response.data.sample_data;
    }
    
    // Fallback to basic data if API fails
    return getBasicFallbackData();
  } catch (error) {
    console.error('Failed to fetch AI sample data:', error);
    // Return basic fallback data
    return getBasicFallbackData();
  }
};

// Basic fallback data in case AI generation fails
const getBasicFallbackData = (): Partial<FormData> => {
  return {
    seller_name: 'Jan Novák',
    seller_address: 'Hlavní 123, 110 00 Praha 1',
    seller_id: '7001010001',
    seller_email: 'jan.novak@email.cz',
    buyer_name: 'Marie Svobodová',
    buyer_address: 'Dlouhá 456, 120 00 Praha 2',
    buyer_id: '8052051234',
    buyer_email: 'marie.svobodova@email.cz',
    payment_method: 'Bankovním převodem',
    delivery_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    delivery_location: 'Dle dohody',
    item_description: 'Předmět prodeje',
    item_category: 'Kategorie',
    item_condition: 'Dobrý',
    price: 10000
  };
};

interface FormData {
  // Contract type
  contract_type?: string;
  contract_subtype?: string;
  
  // Seller info
  seller_name: string;
  seller_address: string;
  seller_id: string;
  seller_email?: string;
  seller_business_id?: string;
  
  // Buyer info
  buyer_name: string;
  buyer_address: string;
  buyer_id: string;
  buyer_email?: string;
  buyer_business_id?: string;
  
  // Item details
  item_description: string;
  item_category?: string;
  item_condition?: string;
  serial_number?: string;
  animal_registration_number?: string;
  warranty_info?: string;
  
  // Dynamic fields from AI - will be added at runtime
  [key: string]: any;
  
  // Payment
  price: number;
  payment_method: string;
  payment_terms?: string;
  
  // Additional
  delivery_date?: string;
  delivery_location?: string;
  special_conditions?: string;
}

const steps = [
  'Typ smlouvy',
  'Kdo prodává?',
  'Kdo kupuje?', 
  'Co se prodává?',
  'Za kolik a jak?',
  'Dodatečné podmínky'
];

const AppleStylePurchaseContractForm: React.FC<AppleStylePurchaseContractFormProps> = ({
  open,
  onClose,
  onSuccess,
  onPreview,
  editMode = false,
  existingData = null,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [incompleteSteps, setIncompleteSteps] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [dynamicFields, setDynamicFields] = useState<any[]>([]);
  const [dynamicClauses, setDynamicClauses] = useState<any[]>([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [autoPreviewTriggered, setAutoPreviewTriggered] = useState(false);
  const [customSubtype, setCustomSubtype] = useState<string>('');
  const [editableFields, setEditableFields] = useState<any[]>([]);
  const [processedItemDescription, setProcessedItemDescription] = useState<string>('');

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: existingData || getBasicFallbackData()
  });

  const watchedData = watch();

  // Initialize edit mode - set all steps as completed if we have existing data
  useEffect(() => {
    if (editMode && existingData && open) {
      // Set all steps except the last one as completed (so user can navigate)
      const allStepsExceptLast = Array.from({ length: steps.length - 1 }, (_, i) => i);
      setCompletedSteps(allStepsExceptLast);
      setCurrentStep(steps.length - 1); // Start on the last step (review)
    }
  }, [editMode, existingData, open, steps.length]);

  // RAG functionality
  const {
    isAvailable: ragAvailable,
    isLoading: ragLoading,
    recommendations,
    legalEnhancements,
    enhanceContract,
    clearRecommendations,
  } = useRAG();

  // Auto-save functionality
  useEffect(() => {
    if (open && currentStep > 0) {
      setAutoSaveStatus('saving');
      const timeoutId = setTimeout(() => {
        // Simulate auto-save
        localStorage.setItem('purchaseContract_draft', JSON.stringify(getValues()));
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [watchedData, currentStep, open, getValues]);

  // Enhanced contract analysis
  useEffect(() => {
    const { price, item_description, payment_method } = watchedData;
    
    if (price && item_description && payment_method && open && ragAvailable) {
      const timeoutId = setTimeout(() => {
        enhanceContract('purchase', watchedData);
      }, 1500);

      return () => clearTimeout(timeoutId);
    }
  }, [watchedData.price, watchedData.item_description, watchedData.payment_method, open, ragAvailable, enhanceContract]);

  const handleNext = async () => {
    const stepFields = getStepFields(currentStep);
    const isStepValid = stepFields.length === 0 ? true : await trigger(stepFields as string[]);
    
    if (isStepValid) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Final submit
        handleFinalSubmit();
      }
    }
  };

  // Auto-open preview on last step when preview is ready
  useEffect(() => {
    const lastIndex = steps.length - 1;
    if (currentStep === lastIndex && previewReady && !autoPreviewTriggered) {
      setAutoPreviewTriggered(true);
      handlePreviewOnly();
    }
    if (currentStep !== lastIndex) {
      setAutoPreviewTriggered(false);
    }
  }, [currentStep, steps.length, previewReady]);

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      if (ragAvailable) {
        await enhanceContract('purchase', getValues());
      }

      // First generate preview
      const previewResponse = await api.post('/documents/test/preview/purchase-contract', getValues());
      // Add metadata for contract modification
      const previewData = {
        ...previewResponse.data,
        contract_type: 'purchase',
        form_data: getValues()
      };
      onPreview(previewData);

      // Then generate PDF document
      const pdfResponse = await api.post('/documents/test/generate/purchase-contract', getValues());
      // Add form_data for download functionality
      const successData = {
        ...pdfResponse.data,
        form_data: getValues()
      };
      onSuccess(successData);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Generování dokumentu se nezdařilo'));
    } finally {
      setLoading(false);
    }
  };

  // Preview-only generation (no final PDF)
  const handlePreviewOnly = async () => {
    setLoading(true);
    setError('');
    try {
      const previewResponse = await api.post('/documents/test/preview/purchase-contract', getValues());
      const previewData = {
        ...previewResponse.data,
        contract_type: 'purchase',
        form_data: getValues()
      };
      onPreview(previewData);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Generování náhledu se nezdařilo'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      if (!editMode) {
        // Pouze resetovat pokud nejsme v edit modu
        reset();
        setCurrentStep(0);
        setCompletedSteps([]);
        setIncompleteSteps([]);
        setError('');
        clearRecommendations();
      }
      onClose();
    }
  };

  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick') return;
    handleClose();
  };

  const getStepFields = (step: number): (keyof FormData)[] => {
    switch (step) {
      case 0: return ['contract_type', 'contract_subtype'];
      case 1: return ['seller_name', 'seller_address', 'seller_id'];
      case 2: return ['buyer_name', 'buyer_address', 'buyer_id'];
      case 3: return ['item_description'];
      case 4: return ['price', 'payment_method'];
      case 5: return [];
      case 6: return [];
      default: return [];
    }
  };

  const canGoNext = () => {
    // Pokud se generuje AI content, nelze pokračovat
    if (aiGenerating) {
      return false;
    }
    
    // Na kroku 0 (výběr typu smlouvy), musíme čekat na preview
    if (currentStep === 0) {
      // Musí být vybrán typ a podtyp a preview musí být připraven
      const values = getValues();
      return !!(values.contract_type && values.contract_subtype && previewReady);
    }
    
    const stepFields = getStepFields(currentStep);
    
    // Pokud krok nemá žádná povinná pole, vždy lze pokračovat
    if (stepFields.length === 0) {
      return true;
    }
    
    const values = getValues();
    const isValid = stepFields.every(field => {
      const value = values[field];
      return value !== undefined && value !== '' && value !== 0;
    });
    
    return isValid;
  };
  
  // Update incomplete steps when form changes
  useEffect(() => {
    const updateIncompleteSteps = () => {
      const newIncompleteSteps: number[] = [];
      
      for (let i = 0; i <= Math.max(currentStep, ...completedSteps); i++) {
        const stepFields = getStepFields(i);
        const values = getValues();
        
        const isStepValid = stepFields.every(field => {
          const value = values[field];
          return value !== undefined && value !== '' && value !== 0;
        });
        
        if (!isStepValid && !completedSteps.includes(i) && i !== currentStep) {
          newIncompleteSteps.push(i);
        }
      }
      
      setIncompleteSteps(newIncompleteSteps);
    };
    
    const subscription = watch(updateIncompleteSteps);
    return () => subscription.unsubscribe();
  }, [watch, currentStep, completedSteps, getValues]);

  // Removed automatic generation - now it's triggered by button click
  
  // Removed automatic sample data fetching - now part of generateFieldsForContractType
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!open) return;
      
      if (event.key === 'Enter' && event.ctrlKey && canGoNext()) {
        handleNext();
      } else if (event.key === 'Escape') {
        if (currentStep > 0) {
          handleBack();
        } else {
          handleClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [open, currentStep, canGoNext]);

  // State pro sledování hodnot editovatelných polí
  const [editableValues, setEditableValues] = useState<{[key: string]: any}>({});
  
  // Funkce pro renderování editovatelného pole podle typu
  const renderEditableField = (field: any) => {
    const fieldKey = `editable_${field.id}`;
    // Použít hodnotu z editableValues, nebo defaultní hodnotu z field.value
    const fieldValue = editableValues[fieldKey] !== undefined ? editableValues[fieldKey] : (field.value || '');
    
    // Date picker pro datumy
    if (field.type === 'date') {
      return (
        <TextField
          key={field.id}
          type="date"
          size="small"
          variant="standard"
          value={fieldValue}
          onChange={(e) => {
            setEditableValues(prev => ({...prev, [fieldKey]: e.target.value}));
            setValue(fieldKey, e.target.value);
          }}
          sx={{
            display: 'inline-flex',
            width: 'auto',
            minWidth: '140px',
            mx: 0.5,
            '& .MuiInput-root': {
              color: 'primary.main',
              fontWeight: 600,
              fontSize: 'inherit',
              '&:before': {
                borderBottomStyle: 'dotted',
              },
            },
          }}
          InputLabelProps={{ shrink: true }}
          title={field.label}
        />
      );
    }
    
    // Select pro výběr z možností
    if (field.type === 'select' && field.options) {
      return (
        <FormControl key={field.id} variant="standard" size="small" sx={{ display: 'inline-flex', minWidth: 80, mx: 0.5 }}>
          <Select
            value={fieldValue}
            onChange={(e) => {
            setEditableValues(prev => ({...prev, [fieldKey]: e.target.value}));
            setValue(fieldKey, e.target.value);
          }}
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              fontSize: 'inherit',
              '&:before': {
                borderBottomStyle: 'dotted',
              },
            }}
          >
            {field.options.map((option: string) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    // Number field pro čísla s validací
    if (field.type === 'number') {
      return (
        <>
          <TextField
            key={field.id}
            type="number"
            size="small"
            variant="standard"
            value={fieldValue}
            onChange={(e) => {
              const val = e.target.value;
              // Validace min/max
              if (field.validation) {
                if (field.validation.min !== undefined && Number(val) < field.validation.min) return;
                if (field.validation.max !== undefined && Number(val) > field.validation.max) return;
              }
              setEditableValues(prev => ({...prev, [fieldKey]: val}));
              setValue(fieldKey, val);
            }}
            sx={{
              display: 'inline-flex',
              width: 'auto',
              minWidth: field.suffix ? '80px' : '100px',
              maxWidth: '150px',
              mx: 0.5,
              '& .MuiInput-root': {
                color: 'primary.main',
                fontWeight: 600,
                fontSize: 'inherit',
                '&:before': {
                  borderBottomStyle: 'dotted',
                },
              },
              '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                display: 'none',
              },
            }}
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
            }}
            placeholder={field.placeholder}
            title={field.label}
          />
          {field.suffix && <span style={{ marginLeft: '2px' }}>{field.suffix}</span>}
        </>
      );
    }
    
    // Default text field
    return (
      <TextField
        key={field.id}
        size="small"
        variant="standard"
        value={fieldValue}
        onChange={(e) => {
          setEditableValues(prev => ({...prev, [fieldKey]: e.target.value}));
          setValue(fieldKey, e.target.value);
        }}
        sx={{
          display: 'inline-flex',
          width: 'auto',
          minWidth: '100px',
          mx: 0.5,
          '& .MuiInput-root': {
            color: 'primary.main',
            fontWeight: 600,
            fontSize: 'inherit',
            '&:before': {
              borderBottomStyle: 'dotted',
            },
          },
        }}
        placeholder={field.placeholder}
        title={field.label}
      />
    );
  };

  // Funkce pro renderování textu s editovatelnými poli
  const renderEditableText = (text: string) => {
    if (!editableFields || editableFields.length === 0) {
      return text;
    }

    const processedText = text;
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    
    // Nahradit placeholdery editovatelnými poli
    editableFields.forEach((field) => {
      const placeholder = `{{${field.id}}}`;
      const index = processedText.indexOf(placeholder);
      
      if (index !== -1) {
        // Přidat text před polem
        if (index > lastIndex) {
          elements.push(processedText.substring(lastIndex, index));
        }
        
        // Přidat editovatelné pole podle typu
        elements.push(renderEditableField(field));
        
        lastIndex = index + placeholder.length;
      }
    });
    
    // Přidat zbývající text
    if (lastIndex < processedText.length) {
      elements.push(processedText.substring(lastIndex));
    }
    
    return elements.length > 0 ? <>{elements}</> : text;
  };

  const renderLivePreview = () => {
    const values = getValues();
    
    // Kontrola, zda jsou vygenerovaná pole - zobrazit náhled pouze po vygenerování polí
    if (!dynamicFields || dynamicFields.length === 0) {
      return (
        <Card sx={{ 
          p: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(249,250,251,0.9) 0%, rgba(255,255,255,0.95) 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              📄 Náhled smlouvy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Náhled smlouvy bude k dispozici po upřesnění typu a předmětu koupě
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Vyberte prosím typ smlouvy v kroku 1
            </Typography>
          </Box>
        </Card>
      );
    }
    
    // Získat název typu smlouvy pro titulek
    const getContractTitle = () => {
      const type = values.contract_type;
      const subtype = values.contract_subtype;
      
      if (type === 'vozidlo') {
        if (subtype === 'auto') return 'KUPNÍ SMLOUVA NA AUTOMOBIL';
        if (subtype === 'motorka') return 'KUPNÍ SMLOUVA NA MOTOCYKL';
        return 'KUPNÍ SMLOUVA NA VOZIDLO';
      } else if (type === 'nemovitost') {
        if (subtype === 'byt') return 'KUPNÍ SMLOUVA NA BYT';
        if (subtype === 'dum') return 'KUPNÍ SMLOUVA NA RODINNÝ DŮM';
        if (subtype === 'pozemek') return 'KUPNÍ SMLOUVA NA POZEMEK';
        return 'KUPNÍ SMLOUVA NA NEMOVITOST';
      } else if (type === 'zvirata') {
        return 'KUPNÍ SMLOUVA NA ZVÍŘE';
      } else if (type === 'movita_vec') {
        return 'KUPNÍ SMLOUVA NA MOVITOU VĚC';
      }
      return 'KUPNÍ SMLOUVA';
    };

    // Získat specifické klauzule podle typu smlouvy
    const getTypeSpecificClauses = () => {
      const clauses: React.ReactElement[] = [];
      
      // Zobrazit dynamická pole z AI pokud existují
      if (dynamicFields && dynamicFields.length > 0) {
        const fieldsToDisplay = dynamicFields.filter((field: any) => values[field.name]);
        
        if (fieldsToDisplay.length > 0) {
          clauses.push(
            <Box key="dynamic-fields" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Specifické údaje:
              </Typography>
              <Typography paragraph sx={{ pl: 2, fontSize: '0.85rem' }}>
                {fieldsToDisplay.map((field: any) => (
                  <React.Fragment key={field.name}>
                    {field.label}: {values[field.name]}
                    <br/>
                  </React.Fragment>
                ))}
              </Typography>
            </Box>
          );
        }
      }
      
      // Zobrazit dynamické klauzule z AI
      if (dynamicClauses && dynamicClauses.length > 0) {
        dynamicClauses.forEach((clause: any, index: number) => {
          clauses.push(
            <Box key={`clause-${index}`} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {clause.title}:
              </Typography>
              <Typography paragraph sx={{ pl: 2, fontSize: '0.85rem' }}>
                {clause.content}
              </Typography>
            </Box>
          );
        });
      }

      // Přidat obecné právní klauzule vždy
      clauses.push(
        <Box key="legal-clauses" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Závěrečná ustanovení:
          </Typography>
          <Typography paragraph sx={{ pl: 2, fontSize: '0.85rem' }}>
            • Kupující prohlašuje, že si předmět koupě řádně prohlédl a je mu znám jeho stav
            <br/>• Vlastnické právo přechází na kupujícího okamžikem zaplacení kupní ceny
            <br/>• Nebezpečí škody přechází na kupujícího převzetím předmětu koupě
            <br/>• Tato smlouva je vyhotovena ve dvou stejnopisech
            <br/>• Smlouva nabývá platnosti a účinnosti dnem podpisu oběma stranami
          </Typography>
        </Box>
      );

      return clauses;
    };
    
    return (
      <Card sx={{ 
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        background: 'white'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057' }}>
            📄 Náhled smlouvy
          </Typography>
          <Typography variant="caption" sx={{ color: '#6c757d' }}>
            {values.contract_type ? `Typ: ${getContractTypeDescription()}` : 'Aktualizuje se při vyplňování'}
          </Typography>
        </Box>
        
        <Box sx={{ p: 3, fontSize: '0.85rem', lineHeight: 1.6 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
            {getContractTitle()}
          </Typography>
          
          <Typography paragraph sx={{ fontSize: '0.8rem', color: '#6c757d' }}>
            podle § 2079 a násl. zákona č. 89/2012 Sb., občanský zákoník
            {values.contract_type === 'nemovitost' && ', ve spojení s katastrálním zákonem'}
            {values.contract_type === 'vozidlo' && ', ve spojení se zákonem o podmínkách provozu vozidel na pozemních komunikacích'}
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Prodávající:
          </Typography>
          <Typography paragraph sx={{ pl: 2, color: values.seller_name ? 'inherit' : '#adb5bd' }}>
            {values.seller_name || '[Jméno prodávajícího]'}<br/>
            {values.seller_address || '[Adresa prodávajícího]'}<br/>
            {values.seller_id && `R.č./IČO: ${values.seller_id}`}
            {values.seller_email && <><br/>E-mail: {values.seller_email}</>}
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Kupující:
          </Typography>
          <Typography paragraph sx={{ pl: 2, color: values.buyer_name ? 'inherit' : '#adb5bd' }}>
            {values.buyer_name || '[Jméno kupujícího]'}<br/>
            {values.buyer_address || '[Adresa kupujícího]'}<br/>
            {values.buyer_id && `R.č./IČO: ${values.buyer_id}`}
            {values.buyer_email && <><br/>E-mail: {values.buyer_email}</>}
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Předmět koupě:
          </Typography>
          <Box sx={{ pl: 2, color: values.item_description ? 'inherit' : '#adb5bd', mb: 2 }}>
            <Typography component="div" paragraph>
              {processedItemDescription ? renderEditableText(processedItemDescription) : 
               (values.item_description || `[Popis ${values.contract_type === 'vozidlo' ? 'vozidla' : values.contract_type === 'nemovitost' ? 'nemovitosti' : 'předmětu koupě'}]`)}
            </Typography>
            {values.item_category && <Typography>Kategorie: {values.item_category}</Typography>}
            {values.item_condition && <Typography>Stav: {values.item_condition}</Typography>}
            {(
              values.contract_type === 'zvirata' ||
              ['pes', 'kocka', 'kun', 'hospodarsky', 'exoticke', 'ostatni_zvirata'].includes((values as any).contract_subtype) ||
              /(kočk|kock|pes|psa|psí|kůň|kun|zvíř)/i.test(values.item_description || '')
            ) ? (
              values.animal_registration_number && (
                <Typography>Evidenční číslo: {values.animal_registration_number}</Typography>
              )
            ) : (
              values.serial_number && <Typography>Sériové číslo: {values.serial_number}</Typography>
            )}
            {values.warranty_info && <Typography>Záruka: {values.warranty_info}</Typography>}
          </Box>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Kupní cena a platební podmínky:
          </Typography>
          <Typography paragraph sx={{ pl: 2, color: values.price ? 'inherit' : '#adb5bd' }}>
            Kupní cena: <strong>{values.price ? `${values.price.toLocaleString()} Kč` : '[Kupní cena]'}</strong><br/>
            Způsob platby: {values.payment_method || '[Způsob platby]'}
            {values.payment_terms && <><br/>{values.payment_terms}</>}
          </Typography>
          
          {(values.delivery_date || values.delivery_location) && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Předání:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.delivery_date && <>Datum předání: {new Date(values.delivery_date).toLocaleDateString('cs-CZ')}<br/></>}
                {values.delivery_location && <>Místo předání: {values.delivery_location}</>}
              </Typography>
            </>
          )}
          
          {/* Přidat specifické klauzule podle typu smlouvy */}
          {getTypeSpecificClauses()}
          
          {values.special_conditions && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                Zvláštní podmínky:
              </Typography>
              <Typography paragraph sx={{ pl: 2 }}>
                {values.special_conditions}
              </Typography>
            </>
          )}
          
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
              {values.contract_type && values.contract_subtype 
                ? `Smlouva typu "${getContractTypeDescription()}" bude dokončena po vyplnění všech povinných údajů...`
                : 'Smlouva bude dokončena po vyplnění všech povinných údajů...'}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  // AI generování polí podle typu smlouvy
  const generateFieldsForContractType = async (type: string, subtype: string) => {
    setAiGenerating(true);
    setPreviewReady(false); // Reset preview state when generating new fields
    try {
      // 1. Generate fields and clauses
      const response = await api.post('/documents/generate-fields', {
        contract_type: type,
        contract_subtype: subtype
      });
      // Normalize AI fields to avoid showing 'sériové číslo' for animals
      let fields = response.data.fields || [];
      if (type === 'zvirata') {
        fields = fields
          .map((f: any) => {
            const name = (f?.name || '').toString().toLowerCase();
            const label = (f?.label || '').toString();
            const isSerial = name.includes('serial') || /sériov/i.test(label);
            if (isSerial) {
              return {
                ...f,
                name: 'animal_registration_number',
                label: 'Evidenční číslo (čip)',
                helpText: 'Identifikace zvířete (číslo čipu nebo evidenční číslo registru)'
              };
            }
            return f;
          })
          .filter((f: any, idx: number, arr: any[]) => arr.findIndex((x: any) => x.name === f.name) === idx);
      }
      setDynamicFields(fields);
      setDynamicClauses(response.data.clauses || []);
      
      // 2. Fetch sample data
      if (!existingData) {
        try {
          const sampleData = await fetchAISampleData(type, subtype);

          // Debug: Log what we received from backend
          console.log('=== AI Sample Data Received ===');
          console.log('Contract Type:', type, '-', subtype);
          console.log('Sample Data:', JSON.stringify(sampleData, null, 2));
          console.log('Seller Name:', sampleData.seller_name);
          console.log('Seller Address:', sampleData.seller_address);
          console.log('===============================');

          // Get current form values
          const currentValues = getValues();

          // Merge sample data with current values (keep contract_type and contract_subtype)
          const newValues: any = {
            ...currentValues,
            ...sampleData,
            contract_type: type,  // Keep the selected contract type
            contract_subtype: subtype  // Keep the selected subtype
          };

          // For animals, map any 'serial_number' sample to 'animal_registration_number'
          if (type === 'zvirata' && newValues.serial_number && !newValues.animal_registration_number) {
            newValues.animal_registration_number = newValues.serial_number;
            delete newValues.serial_number;
          }

          console.log('Resetting form with merged values...');
          console.log('New seller_name:', newValues.seller_name);
          console.log('New seller_address:', newValues.seller_address);

          // Use reset() instead of setValue() to properly update defaultValues
          reset(newValues, {
            keepDefaultValues: false,  // Replace defaultValues completely
            keepDirty: false,
            keepTouched: false
          });

          console.log('Form reset complete');
          
          // 3. Extract editable fields from item_description
          const itemDescription = sampleData.item_description || sampleData.sample_data?.item_description;
          if (itemDescription) {
            try {
              const extractResponse = await api.post('/documents/extract-editable-fields', {
                text: itemDescription,
                context_type: `${type} - ${subtype}`
              });
              
              if (extractResponse.data) {
                setEditableFields(extractResponse.data.editable_fields || []);
                setProcessedItemDescription(extractResponse.data.processed_text || itemDescription);
                
                // Set initial values for editable fields
                const initialValues: {[key: string]: any} = {};
                extractResponse.data.editable_fields?.forEach((field: any) => {
                  const fieldKey = `editable_${field.id}`;
                  initialValues[fieldKey] = field.value;
                  setValue(fieldKey, field.value);
                });
                setEditableValues(initialValues);
              }
            } catch (error) {
              console.error('Failed to extract editable fields:', error);
              setProcessedItemDescription(itemDescription);
            }
          }
        } catch (error) {
          console.error('Failed to fetch sample data:', error);
        }
      }
      
      // 3. Set values for dynamic fields
      if (response.data.fields) {
        response.data.fields.forEach((field: any) => {
          if (!getValues(field.name)) {
            setValue(field.name, field.defaultValue || '');
          }
        });
      }
      
      // Mark preview as ready after fields are generated
      if (response.data.fields && response.data.fields.length > 0) {
        setPreviewReady(true);
      }
    } catch (err) {
      console.error('Chyba při generování polí', err);
      setError('Nepodařilo se vygenerovat pole formuláře');
      setPreviewReady(false);
    } finally {
      setAiGenerating(false);
    }
  };

  const renderSubtypeOptions = () => {
    const type = watchedData.contract_type;
    
    switch (type) {
      case 'movita_vec':
        return [
          <MenuItem key="elektronika" value="elektronika">💻 Elektronika a technika</MenuItem>,
          <MenuItem key="nabytek" value="nabytek">🪑 Nábytek a vybavení</MenuItem>,
          <MenuItem key="obleceni" value="obleceni">👕 Oblečení a obuv</MenuItem>,
          <MenuItem key="sportovni" value="sportovni">⚽ Sportovní vybavení</MenuItem>,
          <MenuItem key="nastroje" value="nastroje">🔧 Nářadí a nástroje</MenuItem>,
          <MenuItem key="ostatni" value="ostatni">📦 Ostatní movité věci</MenuItem>,
          <MenuItem key="vlastni" value="vlastni">✏️ Vlastní typ</MenuItem>,
        ];
      case 'nemovitost':
        return [
          <MenuItem key="byt" value="byt">🏢 Byt v osobním vlastnictví</MenuItem>,
          <MenuItem key="dum" value="dum">🏠 Rodinný dům</MenuItem>,
          <MenuItem key="pozemek" value="pozemek">🌳 Pozemek</MenuItem>,
          <MenuItem key="chata" value="chata">🏡 Chata/chalupa</MenuItem>,
          <MenuItem key="garaz" value="garaz">🚗 Garáž/garážové stání</MenuItem>,
          <MenuItem key="komercni" value="komercni">🏪 Komerční nemovitost</MenuItem>,
          <MenuItem key="vlastni" value="vlastni">✏️ Vlastní typ</MenuItem>,
        ];
      case 'vozidlo':
        return [
          <MenuItem key="auto" value="auto">🚗 Osobní automobil</MenuItem>,
          <MenuItem key="motorka" value="motorka">🏍️ Motocykl</MenuItem>,
          <MenuItem key="nakladni" value="nakladni">🚚 Nákladní vozidlo</MenuItem>,
          <MenuItem key="prives" value="prives">🚐 Přívěs/návěs</MenuItem>,
          <MenuItem key="lod" value="lod">⛵ Loď/člun</MenuItem>,
          <MenuItem key="specialni" value="specialni">🚜 Speciální vozidlo</MenuItem>,
          <MenuItem key="vlastni" value="vlastni">✏️ Vlastní typ</MenuItem>,
        ];
      case 'zvirata':
        return [
          <MenuItem key="pes" value="pes">🐕 Pes</MenuItem>,
          <MenuItem key="kocka" value="kocka">🐈 Kočka</MenuItem>,
          <MenuItem key="kun" value="kun">🐎 Kůň</MenuItem>,
          <MenuItem key="hospodarsky" value="hospodarsky">🐄 Hospodářské zvíře</MenuItem>,
          <MenuItem key="exoticke" value="exoticke">🦜 Exotické zvíře</MenuItem>,
          <MenuItem key="ostatni_zvirata" value="ostatni_zvirata">🐾 Ostatní zvířata</MenuItem>,
          <MenuItem key="vlastni" value="vlastni">✏️ Vlastní typ</MenuItem>,
        ];
      default:
        return [
          <MenuItem key="vlastni" value="vlastni">✏️ Vlastní typ</MenuItem>,
        ];
    }
  };

  const getContractTypeDescription = () => {
    const type = watchedData.contract_type;
    const subtype = watchedData.contract_subtype;
    
    if (!type || !subtype) return '';
    
    const descriptions: any = {
      'movita_vec': {
        'elektronika': 'Kupní smlouva na elektroniku - bude obsahovat pole pro sériové číslo, záruku, technické parametry',
        'nabytek': 'Kupní smlouva na nábytek - bude obsahovat pole pro rozměry, materiál, stav opotřebení',
        'obleceni': 'Kupní smlouva na oblečení - bude obsahovat pole pro velikost, značku, materiál',
      },
      'nemovitost': {
        'byt': 'Kupní smlouva na byt - bude obsahovat katastrální údaje, energetický štítek, vybavení',
        'dum': 'Kupní smlouva na rodinný dům - bude obsahovat pozemek, stavbu, příslušenství',
        'pozemek': 'Kupní smlouva na pozemek - bude obsahovat výměru, druh pozemku, územní plán',
      },
      'vozidlo': {
        'auto': 'Kupní smlouva na automobil - bude obsahovat VIN, STK, počet najetých km, servisní historii',
        'motorka': 'Kupní smlouva na motocykl - bude obsahovat technické údaje, STK, výbavu',
      }
    };
    
    return descriptions[type]?.[subtype] || 'Standardní kupní smlouva';
  };

  const getCustomTypePlaceholder = () => {
    const type = watchedData.contract_type;
    
    if (!type) return 'např. Zadejte typ předmětu...';
    
    const placeholders: any = {
      'movita_vec': 'např. Hodiny, Šperky, Knihy, Hudební nástroj...',
      'nemovitost': 'např. Zahrada, Sklep, Podíl na nemovitosti, Stavební parcela...',
      'vozidlo': 'např. Karavan, Obytný vůz, Elektrokolo, Traktor...',
      'zvirata': 'např. Plazi, Akvarijní ryby, Včelstvo, Chovný dobytek...',
      'podil': 'např. Obchodní podíl v s.r.o., Členský podíl v družstvu...',
      'cenne_papiry': 'např. Akcie, Dluhopisy, Podílové listy, Směnky...',
      'prava': 'např. Autorská práva, Licence, Patenty, Pohledávky...',
      'sberatelske': 'např. Známky, Mince, Obrazy, Starožitnosti, Vinylové desky...',
      'digitalni': 'např. Doména, Software licence, NFT, Kryptoměna, Digitální umění...',
    };
    
    return placeholders[type] || 'např. Zadejte typ předmětu...';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <FormStep
            title="Typ kupní smlouvy"
            subtitle="Vyberte, co chcete koupit/prodat"
            icon={<span aria-hidden="true" style={{ fontSize: 28 }}>📝</span>}
            color="primary"
            show={true}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="contract_type"
                  control={control}
                  rules={{ required: 'Typ smlouvy je povinný' }}
                  render={({ field }) => (
                    <StyledFormControl fullWidth error={!!errors.contract_type}>
                      <InputLabel>Hlavní typ předmětu koupě</InputLabel>
                      <StyledSelect
                        {...field}
                        label="Hlavní typ předmětu koupě"
                        sx={{ borderRadius: 3 }}
                        onChange={(e) => {
                          field.onChange(e);
                          // Reset subtype when main type changes
                          setValue('contract_subtype', '');
                          setPreviewReady(false); // Reset preview state when changing main type
                          setDynamicFields([]); // Clear previous fields
                          setDynamicClauses([]); // Clear previous clauses
                        }}
                      >
                        <MenuItem value="movita_vec">🪑 Movitá věc (nábytek, elektronika, oblečení)</MenuItem>
                        <MenuItem value="nemovitost">🏠 Nemovitost (dům, byt, pozemek)</MenuItem>
                        <MenuItem value="vozidlo">🚗 Vozidlo (auto, motorka, přívěs)</MenuItem>
                        <MenuItem value="podil">💼 Obchodní podíl ve společnosti</MenuItem>
                        <MenuItem value="cenne_papiry">📈 Cenné papíry (akcie, dluhopisy)</MenuItem>
                        <MenuItem value="prava">⚖️ Práva a pohledávky</MenuItem>
                        <MenuItem value="zvirata">🐕 Zvířata</MenuItem>
                        <MenuItem value="sberatelske">🎨 Sběratelské předměty (umění, mince)</MenuItem>
                        <MenuItem value="digitalni">💻 Digitální aktiva (software, domény)</MenuItem>
                      </StyledSelect>
                    </StyledFormControl>
                  )}
                />
              </Box>

              {watchedData.contract_type && (
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Controller
                    name="contract_subtype"
                    control={control}
                    rules={{ required: 'Upřesnění typu je povinné' }}
                    render={({ field }) => (
                      <StyledFormControl fullWidth error={!!errors.contract_subtype}>
                        <InputLabel>Upřesnění typu</InputLabel>
                        <StyledSelect
                          {...field}
                          label="Upřesnění typu"
                          sx={{ borderRadius: 3 }}
                          onChange={(e) => {
                            field.onChange(e);
                            // Reset preview when changing subtype
                            setPreviewReady(false);
                            setDynamicFields([]);
                            setDynamicClauses([]);
                            // Clear custom subtype if not selecting "vlastni"
                            if (e.target.value !== 'vlastni') {
                              setCustomSubtype('');
                            }
                          }}
                        >
                          {renderSubtypeOptions()}
                        </StyledSelect>
                      </StyledFormControl>
                    )}
                  />
                </Box>
              )}

              {/* Vlastní typ - textové pole */}
              {watchedData.contract_subtype === 'vlastni' && (
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Controller
                    name="custom_subtype_name"
                    control={control}
                    rules={{ required: 'Zadejte název vlastního typu' }}
                    render={({ field }) => (
                      <SmartTextField
                        {...field}
                        label="Zadejte vlastní typ předmětu"
                        placeholder={getCustomTypePlaceholder()}
                        control={control}
                        helperText="Popište co chcete prodat/koupit"
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCustomSubtype(e.target.value);
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* Tlačítko Generovat náhled */}
              {watchedData.contract_type && watchedData.contract_subtype && (
                <Box sx={{ gridColumn: 'span 12', textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      const subtype = watchedData.contract_subtype === 'vlastni' 
                        ? watchedData.custom_subtype_name || customSubtype
                        : watchedData.contract_subtype;
                      if (subtype) {
                        generateFieldsForContractType(
                          watchedData.contract_type!,
                          subtype
                        );
                      }
                    }}
                    disabled={aiGenerating || (watchedData.contract_subtype === 'vlastni' && !watchedData.custom_subtype_name && !customSubtype)}
                    startIcon={aiGenerating ? <CircularProgress size={20} /> : null}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      background: aiGenerating 
                        ? 'linear-gradient(135deg, #e0e0e0, #bdbdbd)'
                        : 'linear-gradient(135deg, #1976d2, #1565c0)',
                      boxShadow: aiGenerating ? 'none' : '0 8px 25px rgba(0,0,0,0.15)',
                      '&:hover': {
                        boxShadow: aiGenerating ? 'none' : '0 12px 35px rgba(0,0,0,0.2)',
                        transform: aiGenerating ? 'none' : 'translateY(-2px)',
                      },
                      '&:disabled': {
                        opacity: 1,
                      },
                    }}
                  >
                    {aiGenerating ? 'Generuji náhled...' : '🤖 Generovat náhled'}
                  </Button>
                </Box>
              )}

              {aiGenerating && (
                <Box sx={{ gridColumn: 'span 12', textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    🤖 AI generuje specifická pole pro vybraný typ smlouvy...
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Počkejte prosím, než se vygeneruje náhled smlouvy
                  </Typography>
                </Box>
              )}

              {watchedData.contract_subtype && !aiGenerating && (
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      <strong>Vybraný typ:</strong> {getContractTypeDescription()}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Formulář bude přizpůsoben specifickým požadavkům pro tento typ kupní smlouvy.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          </FormStep>
        );

      case 1:
        return (
          <FormStep
            title="Kdo prodává?"
            subtitle="Zadejte údaje o prodávajícím"
            icon={<span aria-hidden="true" style={{ fontSize: 28 }}>🏢</span>}
            color="primary"
            show={true}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="seller_name"
                  control={control}
                  rules={{ required: 'Jméno prodávajícího je povinné' }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Jméno a příjmení / Název firmy"
                      placeholder="např. Jan Novák nebo ABC s.r.o."
                      examples={['Jan Novák', 'Marie Svobodová', 'TechSoft s.r.o.', 'AutoCZ a.s.']}
                      helpText="Zadejte celé jméno fyzické osoby nebo název firmy"
                      error={!!errors.seller_name}
                      errorMessage={errors.seller_name?.message}
                      validation={{
                        required: true,
                        message: 'Jméno prodávajícího je povinné'
                      }}
                    />
                  )}
                />
              </Box>
              
              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="seller_address"
                  control={control}
                  rules={{ required: 'Adresa prodávajícího je povinná' }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Adresa"
                      multiline
                      rows={2}
                      placeholder="Ulice a číslo, PSČ Město"
                      examples={[
                        'Václavské náměstí 1, 110 00 Praha 1',
                        'Náměstí Svobody 10, 602 00 Brno',
                        'Hlavní 25, 301 00 Plzeň'
                      ]}
                      error={!!errors.seller_address}
                      errorMessage={errors.seller_address?.message}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="seller_id"
                  control={control}
                  rules={{ required: 'Rodné číslo nebo IČO je povinné' }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Rodné číslo / IČO"
                      placeholder="123456/7890 nebo 12345678"
                      examples={['123456/7890', '12345678']}
                      helpText="Rodné číslo pro fyzické osoby, IČO pro firmy"
                      error={!!errors.seller_id}
                      errorMessage={errors.seller_id?.message}
                      validation={{
                        required: true,
                        pattern: /^(\d{6}\/?\d{4}|\d{8})$/,
                        message: 'Neplatný formát rodného čísla nebo IČO'
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="seller_email"
                  control={control}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="E-mail (volitelné)"
                      type="email"
                      placeholder="jmeno@email.cz"
                      helpText="E-mail pro komunikaci ohledně smlouvy"
                    />
                  )}
                />
              </Box>
            </Box>
          </FormStep>
        );

      case 2:
        return (
          <FormStep
            title="Kdo kupuje?"
            subtitle="Zadejte údaje o kupujícím"
            icon={<span aria-hidden="true" style={{ fontSize: 28 }}>🧑</span>}
            color="success"
            show={true}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="buyer_name"
                  control={control}
                  rules={{ required: 'Jméno kupujícího je povinné' }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Jméno a příjmení / Název firmy"
                      placeholder="např. Petra Nováková nebo XYZ s.r.o."
                      examples={['Petra Nováková', 'Tomáš Dvořák', 'Nákupy s.r.o.', 'Retail a.s.']}
                      error={!!errors.buyer_name}
                      errorMessage={errors.buyer_name?.message}
                    />
                  )}
                />
              </Box>
              
              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="buyer_address"
                  control={control}
                  rules={{ required: 'Adresa kupujícího je povinná' }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Adresa"
                      multiline
                      rows={2}
                      placeholder="Ulice a číslo, PSČ Město"
                      examples={[
                        'Wenceslas Square 1, 110 00 Prague 1',
                        'Masaryk Street 15, 602 00 Brno'
                      ]}
                      error={!!errors.buyer_address}
                      errorMessage={errors.buyer_address?.message}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="buyer_id"
                  control={control}
                  rules={{ required: 'Rodné číslo nebo IČO je povinné' }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Rodné číslo / IČO"
                      placeholder="123456/7890 nebo 12345678"
                      error={!!errors.buyer_id}
                      errorMessage={errors.buyer_id?.message}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="buyer_email"
                  control={control}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="E-mail (volitelné)"
                      type="email"
                      placeholder="jmeno@email.cz"
                    />
                  )}
                />
              </Box>
            </Box>
          </FormStep>
        );

      case 3:
        // Dynamické zobrazení polí podle typu smlouvy - používá pole z AI
        const renderDynamicFields = () => {
          if (!dynamicFields || dynamicFields.length === 0) {
            // Pokud ještě nejsou načtená pole z AI, zobrazit loading
            if (aiGenerating) {
              return (
                <Box sx={{ gridColumn: 'span 12', textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    🤖 Generuji specifická pole a vzorová data pro {getContractTypeDescription()}...
                  </Typography>
                </Box>
              );
            }
            return null;
          }
          
          // Renderovat dynamická pole z AI
          return dynamicFields.map((fieldDef: any) => (
            <Box key={fieldDef.name} sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
              <Controller
                name={fieldDef.name}
                control={control}
                rules={{ 
                  required: fieldDef.required ? `${fieldDef.label} je povinné pole` : false,
                  pattern: fieldDef.validation?.pattern ? {
                    value: new RegExp(fieldDef.validation.pattern),
                    message: `Neplatný formát pro ${fieldDef.label}`
                  } : undefined,
                  min: fieldDef.validation?.min,
                  max: fieldDef.validation?.max
                }}
                render={({ field }) => {
                  // Pro select pole
                  if (fieldDef.type === 'select' && fieldDef.options) {
                    return (
                      <StyledFormControl fullWidth>
                        <InputLabel>{fieldDef.label}</InputLabel>
                        <StyledSelect
                          {...field}
                          label={fieldDef.label}
                          sx={{ borderRadius: 3 }}
                        >
                          {fieldDef.options.map((option: string) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </StyledSelect>
                      </StyledFormControl>
                    );
                  }
                  
                  // Pro ostatní pole
                  return (
                    <SmartTextField
                      {...field}
                      label={fieldDef.label}
                      placeholder={fieldDef.placeholder}
                      helpText={fieldDef.helpText}
                      type={fieldDef.type === 'number' ? 'number' : fieldDef.type === 'date' ? 'date' : 'text'}
                      multiline={fieldDef.type === 'textarea'}
                      rows={fieldDef.type === 'textarea' ? 3 : 1}
                      error={!!errors[fieldDef.name]}
                      errorMessage={errors[fieldDef.name]?.message as string}
                    />
                  );
                }}
              />
            </Box>
          ));
        };
        
        return (
          <FormStep
            title="Co se prodává?"
            subtitle="Popište prodávanou věc"
            icon={<span aria-hidden="true" style={{ fontSize: 28 }}>🛒</span>}
            color="warning"
            show={true}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="item_description"
                  control={control}
                  rules={{ required: 'Popis věci je povinný' }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label={`Popis ${watchedData.contract_subtype === 'auto' ? 'vozidla' : 
                               watchedData.contract_subtype === 'byt' ? 'bytu' : 
                               watchedData.contract_subtype === 'dum' ? 'domu' : 'předmětu'}`}
                      multiline
                      rows={3}
                      placeholder={watchedData.contract_type === 'vozidlo' ? 
                        'např. Škoda Octavia 1.6 TDI, černá metalíza, kombi' :
                        watchedData.contract_type === 'nemovitost' ?
                        'např. Byt 3+1, 85m², 3. patro, cihlová zástavba' :
                        'Detailně popište co se prodává...'}
                      helpText="Čím detailnější popis, tím lépe chráněny budou vaše práva"
                      error={!!errors.item_description}
                      errorMessage={errors.item_description?.message}
                    />
                  )}
                />
              </Box>
              
              {/* Dynamická pole podle typu */}
              {renderDynamicFields()}

              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="item_condition"
                  control={control}
                  render={({ field }) => (
                    <StyledFormControl fullWidth>
                      <InputLabel>Stav věci</InputLabel>
                      <StyledSelect
                        {...field}
                        label="Stav věci"
                        sx={{ borderRadius: 3 }}
                      >
                        <MenuItem value="Nový">✨ Nový</MenuItem>
                        <MenuItem value="Velmi dobrý">😊 Velmi dobrý</MenuItem>
                        <MenuItem value="Dobrý">👍 Dobrý</MenuItem>
                        <MenuItem value="Použitý">🔄 Použitý</MenuItem>
                        <MenuItem value="Vystavní kus">🏪 Vystavní kus</MenuItem>
                        <MenuItem value="Na náhradní díly">🔧 Na náhradní díly</MenuItem>
                      </StyledSelect>
                    </StyledFormControl>
                  )}
                />
              </Box>

              {/* Identification number field: serial for general items, evidenční/čip for animals */}
              {(watchedData.contract_type === 'zvirata' ||
                ['pes', 'kocka', 'kun', 'hospodarsky', 'exoticke', 'ostatni_zvirata'].includes((watchedData as any).contract_subtype) ||
                /(kočk|kock|pes|psa|psí|kůň|kun|zvíř)/i.test(watchedData.item_description || '')) ? (
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <Controller
                    name="animal_registration_number"
                    control={control}
                    render={({ field }) => (
                      <SmartTextField
                        {...field}
                        label="Evidenční číslo (čip) – volitelné"
                        placeholder="např. číslo čipu nebo evidenční číslo"
                        helpText="Identifikace zvířete (číslo čipu nebo evidenční číslo registru)"
                      />
                    )}
                  />
                </Box>
              ) : (
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <Controller
                    name="serial_number"
                    control={control}
                    render={({ field }) => (
                      <SmartTextField
                        {...field}
                        label="Výrobní/sériové číslo (volitelné)"
                        placeholder="např. ABC123456789"
                        helpText="Pomáhá při identifikaci věci a záručních nároků"
                      />
                    )}
                  />
                </Box>
              )}

              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="warranty_info"
                  control={control}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Informace o záruce (volitelné)"
                      placeholder="např. Záruka do 31.12.2025, pozor na mechanické poškození"
                      examples={[
                        'Záruka výrobce 24 měsíců od zakoupení',
                        'Záruka již vypršela',
                        'Rozšířená záruka do 31.12.2025'
                      ]}
                    />
                  )}
                />
              </Box>
            </Box>
          </FormStep>
        );

      case 4:
        return (
          <FormStep
            title="Za kolik a jak?"
            subtitle="Finanční podmínky prodeje"
            icon={<span aria-hidden="true" style={{ fontSize: 28 }}>💳</span>}
            color="error"
            show={true}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="price"
                  control={control}
                  rules={{ 
                    required: 'Cena je povinná',
                    min: { value: 1, message: 'Cena musí být větší než 0' }
                  }}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Kupní cena"
                      type="number"
                      placeholder="50000"
                      examples={['5000', '50000', '500000']}
                      helpText="Zadejte částku v českých korunách"
                      error={!!errors.price}
                      errorMessage={errors.price?.message}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="payment_method"
                  control={control}
                  rules={{ required: 'Způsob platby je povinný' }}
                  render={({ field }) => (
                    <StyledFormControl fullWidth error={!!errors.payment_method}>
                      <InputLabel>Způsob platby</InputLabel>
                      <StyledSelect
                        {...field}
                        label="Způsob platby"
                        sx={{ borderRadius: 3 }}
                      >
                        <MenuItem value="Hotově">💰 Hotově při předání</MenuItem>
                        <MenuItem value="Bankovním převodem">🏦 Bankovním převodem</MenuItem>
                        <MenuItem value="Kartou">💳 Platební kartou</MenuItem>
                        <MenuItem value="PayPal">📱 PayPal</MenuItem>
                        <MenuItem value="Kombinace">🔄 Kombinace způsobů</MenuItem>
                      </StyledSelect>
                    </StyledFormControl>
                  )}
                />
              </Box>

              {watchedData.payment_method === 'Kombinace' && (
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Controller
                    name="payment_terms"
                    control={control}
                    render={({ field }) => (
                      <SmartTextField
                        {...field}
                        label="Podrobnosti platby"
                        multiline
                        rows={2}
                        placeholder="např. 50% záloha hotově, zbytek bankovním převodem do 7 dnů"
                        examples={[
                          '50% záloha hotově, zbytek bankovním převodem do 7 dnů',
                          '10 000 Kč záloha, zbytek při převzetí'
                        ]}
                      />
                    )}
                  />
                </Box>
              )}

              <Box sx={{ gridColumn: 'span 12' }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    💡 <strong>Tip:</strong> U hotovostních plateb nad 270 000 Kč je nutné nahlásit platbu finančním úřadem.
                  </Typography>
                </Alert>
              </Box>
            </Box>
          </FormStep>
        );

      case 5:
        return (
          <FormStep
            title="Dodatečné podmínky"
            subtitle="Volitelné informace a zvláštní podmínky"
            icon={<span aria-hidden="true" style={{ fontSize: 28 }}>📝</span>}
            color="secondary"
            show={true}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="delivery_date"
                  control={control}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Datum předání (volitelné)"
                      type="date"
                      helpText="Kdy se uskuteční předání věci"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <Controller
                  name="delivery_location"
                  control={control}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Místo předání (volitelné)"
                      placeholder="např. u prodávajícího, v Praze"
                      examples={[
                        'U prodávajícího (viz adresa výše)',
                        'Praha, náměstí Míru',
                        'Brno, hlavní nádraží'
                      ]}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: 'span 12' }}>
                <Controller
                  name="special_conditions"
                  control={control}
                  render={({ field }) => (
                    <SmartTextField
                      {...field}
                      label="Zvláštní podmínky (volitelné)"
                      multiline
                      rows={3}
                      placeholder="Zde můžete uvést jakékoli dodatečné podmínky..."
                      examples={[
                        'Kupující si věc prohlédl a bere ji v současném stavu',
                        'Věc bude předána včetně originálního balení a příslušenství',
                        'Prodávající poskytuje instruktáž k použití'
                      ]}
                      helpText="Například podmínky předání, dodatečné záruky, apod."
                    />
                  )}
                />
              </Box>
            </Box>
          </FormStep>
        );

      case 6:
        return (
          <FormStep
            title="Kontrola a náhled"
            subtitle="Zkontrolujte zadané údaje před vygenerováním"
            icon={<Check />}
            color="success"
            show={true}
          >
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    📋 Shrnutí smlouvy
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Prodávající
                  </Typography>
                  <Typography variant="body1">
                    {watchedData.seller_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {watchedData.seller_address}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Kupující
                  </Typography>
                  <Typography variant="body1">
                    {watchedData.buyer_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {watchedData.buyer_address}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: 'span 12' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Předmět koupě
                  </Typography>
                  <Typography variant="body1">
                    {watchedData.item_description}
                  </Typography>
                  {watchedData.item_condition && (
                    <Typography variant="body2" color="text.secondary">
                      Stav: {watchedData.item_condition}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Kupní cena
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {watchedData.price?.toLocaleString()} Kč
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Způsob platby
                  </Typography>
                  <Typography variant="body1">
                    {watchedData.payment_method}
                  </Typography>
                </Box>

                {recommendations.length > 0 && (
                  <Box sx={{ gridColumn: 'span 12' }}>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        🤖 AI doporučení
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {recommendations.slice(0, 3).map((rec: any, index: number) => (
                          <li key={index}>
                            <Typography variant="body2">{rec.text || rec}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  </Box>
                )}
              </Box>
            </Box>
          </FormStep>
        );

      default:
        return null;
    }
  };

  return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'stretch',
          },
        }}
        PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          minHeight: '98vh',
          height: '98vh',
          width: '98vw',
          maxWidth: '1600px',
          maxHeight: '98vh',
          m: { xs: 1, sm: 2 },
        }
      }}
    >
        <DialogContent sx={{ p: 4, maxHeight: 'calc(98vh - 64px)', overflow: 'auto', position: 'relative' }}>
          <IconButton
            aria-label="Zavřít formulář"
            onClick={handleClose}
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'grey.600' }}
          >
            <CloseIcon />
          </IconButton>
        {error && (
          <Fade in={!!error}>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 3,
                border: '1px solid rgba(211, 47, 47, 0.2)',
                bgcolor: 'rgba(211, 47, 47, 0.05)'
              }}
            >
              {error}
            </Alert>
          </Fade>
        )}

        <StepperProgress
          steps={steps}
          activeStep={currentStep}
          completedSteps={completedSteps}
          incompleteSteps={incompleteSteps}
          color="success"
          onStepClick={(stepIndex) => {
            // Lze přejít pouze na vyplněné kroky nebo aktuální krok
            if (completedSteps.includes(stepIndex) || stepIndex === currentStep) {
              setCurrentStep(stepIndex);
            }
          }}
        />

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, 
          gap: 4, 
          minHeight: 400 
        }}>
          {/* Formulář */}
          <Box sx={{ minHeight: 400 }}>
            {renderStepContent()}
          </Box>
          
          {/* Live náhled */}
          <Box sx={{ 
            display: { xs: 'none', lg: 'block' },
            position: 'sticky',
            top: 20,
            maxHeight: '60vh',
            overflow: 'auto'
          }}>
            {renderLivePreview()}
          </Box>
        </Box>

        {/* Preview opens automatically on last step; no manual button */}

        <StepNavigation
          currentStep={currentStep}
          totalSteps={steps.length}
          onNext={handleNext}
          onBack={handleBack}
          canGoNext={canGoNext()}
          canGoBack={currentStep > 0 && !aiGenerating}
          isLoading={loading || aiGenerating}
          autoSave={true}
          autoSaveStatus={autoSaveStatus}
          color="primary"
        />
      </DialogContent>
    </Dialog>
  );
};

export default AppleStylePurchaseContractForm;
